function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { useEffect, useMemo, useReducer, useState } from 'react';
import { Redirect, Route, Switch, useHistory } from 'react-router-dom';
import { CloseButton, FunctionalLoader, IconButton, IntentBehaviorProvider, Typography, useDidUpdate, useOpenUnsavedChangesModal, useOverlayContext, usePrevious, useStateInRef } from 'seagull';
import { BrushIcon } from 'seagull/icons';
import IntroVideoStep from "./IntroVideoStep/IntroVideoStep";
import RestfulWrapper from "../../components/RestfulWrapper/RestfulWrapper";
import { ErrorText, routes } from "../../constants";
import { useStore, useStoreActions } from "../../contexts";
import { useEventReporter } from "../../event-reporter";
import { useFeatureFlags } from "../../hooks/useFeatureFlags";
import { useOnMount } from "../../hooks/useOnMount";
import { getIntentBehavior, Intent } from "../../infra/access-control";
import { ErrorResponse, ServerError } from "../../infra/ErrorResponse";
import { handleRequest } from "../../infra/utils";
import { relationshipModel, serviceModel } from "../../models";
import { RequestAccountantAccess, requestAccountantAccessModel } from "../../models/request-accountant-access";
import { BIRelationshipScopeProvider } from "../../providers/BIRelationshipScopeProvider";
import StripeElementsProvider from "../../providers/StripeElementsProvider";
import { selectors } from "../../store/state-selectors";
import { ClientFlowStructure } from "../components/ClientFlowStructure";
import { ResponsiveProvider } from "../contexts";
import AccountantAccessStep from "./AccountantAccessStep/AccountantAccessStep";
import ApproveTermsStep from "./ApproveTermsStep/ApproveTermsStep";
import { BrandingActionsProvider } from "./BrandingActionsContext";
import styles from "./ClientOnboarding.module.scss";
import { clientOnboardingNavigateStateReducer, COBStep, getInitialSelectedBundleId, getInitialSelectedOptionalServices, getInitialSelectedPaymentMethod, getReachableSteps, getStepName, isAllowedToGoBack, PreviewState, Reason, termsOfServiceStepIndex } from "./ClientOnboarding.utils";
import { CobBrandingProvider, useCobBranding } from "./CobBrandingContext";
import { CobConfigProvider } from "./CobConfigContext";
import { CobStorageProvider, useCobStorage } from "./CobStorageContext";
import ConnectPaymentMethodStep from "./ConnectPaymentMethodStep/ConnectPaymentMethodStep";
import PackagesStep from "./PackagesStep/PackagesStep";
import PendingAdditionalSignaturesWithAnimations from "./RelationshipSignedStep/PendingAdditionalSignaturesWithAnimations";
import ReviewServicesStep from "./ReviewServicesStep/ReviewServicesStep";
import { TermsOfServiceStep } from "./TermsOfServiceStep/TermsOfServiceStep";
import WelcomeStep from "./WelcomeStep/WelcomeStep";
import { changeBusinessLoader } from "./WelcomeStep/welcomeStepUtils";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
export function ClientOnboarding(props) {
  const userRole = useStore(selectors.user.role);
  const userId = useStore(selectors.user.id);
  const relationshipScope = {
    id: props.relationship.id,
    isApproved: relationshipModel.isApproved(props.relationship)
  };
  return /*#__PURE__*/_jsx(BIRelationshipScopeProvider, {
    relationshipScope: relationshipScope,
    children: /*#__PURE__*/_jsx(IntentBehaviorProvider, {
      value: _objectSpread({}, getIntentBehavior(userRole, {
        relationshipState: {
          relationship: props.relationship,
          isPreviewMode: !!props.previewState
        },
        userId
      })),
      children: /*#__PURE__*/_jsx(ClientOnboardingInner, _objectSpread({}, props))
    })
  });
}
function ClientOnboardingInner(props) {
  const {
    relationship,
    currentStep,
    previewState,
    disableMobile,
    dismissPreviewModal,
    onPreviewStateChange,
    setCurrentStep,
    refresh
  } = props;
  const [localPreviewState, setLocalPreviewState] = useState(previewState);
  const isPreview = !!localPreviewState;
  const userId = useStore(selectors.user.id);
  const isReviewer = relationshipModel.isReviewer(relationship, userId);
  const featureFlags = useFeatureFlags();
  const isMemberClient = useStore(s => selectors.user.isMemberUser(s) && selectors.user.isClient(s));
  const {
    logBI,
    api
  } = useStoreActions();
  const {
    reportToGtm,
    reportToAppcues,
    reportToHubspot,
    reportToHeap
  } = useEventReporter();
  const {
    getFromStorage,
    setToStorage,
    clearStorage
  } = useCobStorage(relationship);
  const reachableSteps = getReachableSteps(relationship, Boolean(featureFlags.MULTIPLE_LEGAL_TERMS_DOCUMENTS));
  const overlayContext = useOverlayContext();
  const termsOfServices = useMemo(() => {
    var _relationship$legalTe;
    return (_relationship$legalTe = relationship.legalTermsDocuments) === null || _relationship$legalTe === void 0 ? void 0 : _relationship$legalTe.slice(0, 4);
  }, [relationship.legalTermsDocuments]);
  const [termsOfServiceStatements, setTermsOfServiceStatements] = useState(termsOfServices.map(termsOfService => !(termsOfService.termsOfService.type === 'explicitApproval' && termsOfService.termsOfService.explicitApproval.statement)));
  const [prevVisitedSteps, setPrevVisitedSteps] = useState(getFromStorage('prev_visited_steps') || []);
  const activeStepIndex = Math.max(reachableSteps.findIndex(s => s === currentStep), 0);
  const lastActiveStepIndex = usePrevious(activeStepIndex);
  const getStateInRef = useStateInRef({
    prevVisitedSteps,
    prevActiveStepIndex: lastActiveStepIndex,
    setToStorage
  });
  const {
    isDirty,
    revertToInitial
  } = useCobBranding();
  const openUnsavedChangesModal = useOpenUnsavedChangesModal();
  const [approveAgreementStatus, setApproveAgreementStatus] = useState(null);
  const [disabledReason, setDisabledReason] = useState();
  const [selectedPayment, setSelectedPayment] = useState(() => getInitialSelectedPaymentMethod(getFromStorage, relationship, userId));
  const [selectedBundleID, setSelectedBundleID] = useState(() => getInitialSelectedBundleId(getFromStorage, relationship, userId));
  const [selectedOptionalServices, _setSelectedOptionalServices] = useState(() => getInitialSelectedOptionalServices({
    selectedBundleID,
    getFromStorage,
    isPreview,
    relationship,
    setToStorage,
    userId
  }));
  const previewStatus = isPreview ? dismissPreviewModal ? {
    inModal: true,
    onDismissPreviewModal: dismissPreviewModal
  } : {
    inModal: false
  } : undefined;
  async function toggleBrandSetup() {
    if (localPreviewState === PreviewState.SetupBrand) {
      if (isDirty) {
        const confirmed = await openUnsavedChangesModal({
          testid: 'client-preview-unsaved-popup',
          title: featureFlags.SET_UP_BRAND_CLIENT_PREVIEW ? 'Unsaved brand changes' : undefined
        });
        if (confirmed) {
          revertToInitial();
          setLocalPreviewState(previewState);
          onPreviewStateChange === null || onPreviewStateChange === void 0 || onPreviewStateChange(previewState);
        }
      } else {
        setLocalPreviewState(previewState);
        onPreviewStateChange === null || onPreviewStateChange === void 0 || onPreviewStateChange(previewState);
      }
    } else {
      setLocalPreviewState(PreviewState.SetupBrand);
      onPreviewStateChange === null || onPreviewStateChange === void 0 || onPreviewStateChange(PreviewState.SetupBrand);
    }
  }
  const setSelectedOptionalServices = val => {
    _setSelectedOptionalServices(val);
    setToStorage('selected_optional_services', val);
  };
  const completedSteps = prevVisitedSteps.filter(stepIdx => {
    const step = reachableSteps[stepIdx];
    switch (step) {
      case COBStep.selectAPackage:
        return selectedBundleID != null;
      case COBStep.paymentMethod:
        return !!selectedPayment;
      case COBStep.accountantAccess:
        {
          const checked = getFromStorage('request_accountant_access');
          return relationship.requestAccountantAccess !== RequestAccountantAccess.Mandatory || checked;
        }
      case COBStep.termsOfService1:
      case COBStep.termsOfService2:
      case COBStep.termsOfService3:
      case COBStep.termsOfService4:
        return termsOfServiceStatements[termsOfServiceStepIndex[step]];
      default:
        return true;
    }
  });
  useEffect(() => {
    const {
      prevActiveStepIndex,
      prevVisitedSteps,
      setToStorage
    } = getStateInRef();
    if (prevActiveStepIndex != null && !prevVisitedSteps.includes(prevActiveStepIndex)) {
      setPrevVisitedSteps(prevVisited => {
        const newPrevVisitedSteps = [...prevVisited, prevActiveStepIndex];
        setToStorage('prev_visited_steps', newPrevVisitedSteps);
        return newPrevVisitedSteps;
      });
    }
  }, [activeStepIndex]);
  useEffect(() => {
    if (currentStep && currentStep !== COBStep.welcome) {
      if (currentStep === COBStep.allSet) {
        clearStorage();
      } else {
        setToStorage('last_step', currentStep);
      }
    }
  }, [clearStorage, currentStep, setToStorage]);
  useOnMount(() => {
    if (!isPreview) {
      if (relationship.isSample) {
        logBI(11903);
        reportToGtm('notification-open', 'vendor-opened-test-agreement');
        reportToAppcues('notification-open', 'vendor-opened-test-agreement');
        reportToHeap('testAgreement', 'load', 'vendor-opened-test-agreement');
      } else {
        logBI(10824);
        reportToHeap('clientOb', 'load', 'agreement', featureFlags);
      }
      if (window.heap != null) window.heap.addUserProperties(featureFlags);
    }
    if (relationshipModel.hasUserSigned(relationship, userId)) setCurrentStep(COBStep.allSet);
  });
  function changeSelectedPayment(selectedPayment) {
    setSelectedPayment(selectedPayment);
    if (selectedPayment) setToStorage('selected_payment_method', selectedPayment);
  }
  function navigateFrom(currentStep) {
    const currentIdx = reachableSteps.indexOf(currentStep);
    return {
      goNext: () => setCurrentStep(reachableSteps[currentIdx + 1]),
      goBack: () => setCurrentStep(reachableSteps[currentIdx - 1])
    };
  }
  async function handleAgreementSubmission(fundingSourceId, selectedBundleId) {
    const loaderName = 'lastStep';
    overlayContext.startLoader(loaderName);
    const payload = {
      fundingSourceId,
      selectedBundleId,
      selectedOptionalServices: getFromStorage('selected_optional_services'),
      requestAccountantAccessApproved: requestAccountantAccessModel.wasRequested(relationship.requestAccountantAccess)
    };
    const request = relationshipModel.isSignatory(relationship, userId) ? api.relationship.signatoryApprove(relationship, payload) : api.relationship.approve(relationship, payload);
    const {
      success,
      error
    } = await handleRequest(request);
    overlayContext.stopLoader(loaderName);
    if (!success) {
      if (error.hasServerError(ServerError.UserAlreadySigned)) {
        overlayContext.showSuccessToast('You have already signed the agreement');
      } else {
        const errorText = error.hasServerError(ServerError.MismatchingRelationshipVersion) ? ErrorText.MismatchingRelationshipVersion : error.hasServerError(ServerError.InvalidRelationshipStatus) ? 'The proposal was canceled' : null;
        errorText && overlayContext.showErrorToast(errorText);
        throw error;
      }
    }
  }
  useEffect(() => {
    if (!relationship.isSample && reachableSteps.includes(COBStep.paymentMethod) && !selectedPayment) {
      setDisabledReason(Reason.PaymentMethod);
      return;
    }
    if (reachableSteps.includes(COBStep.selectAPackage) && !selectedBundleID) {
      setDisabledReason(Reason.SelectAPackage);
      return;
    }
    if (termsOfServiceStatements.includes(false)) {
      setDisabledReason(Reason.ApproveTermsOfServices);
      return;
    }
    if (!relationshipModel.actualServices(relationship, selectedBundleID).some(service => serviceModel.isMandatory(service.participation) || selectedOptionalServices.includes(service.id))) {
      setDisabledReason(Reason.ReviewServices);
      return;
    }
    setDisabledReason(undefined);
  }, [selectedPayment, relationship, selectedBundleID, selectedOptionalServices, reachableSteps, termsOfServiceStatements]);
  async function approveAgreement() {
    if (relationship.isSample) {
      logBI(11904);
    } else {
      logBI(10558);
    }
    try {
      const navigateToPendingRelationshipPage = () => navigateFrom(currentStep).goNext();
      setApproveAgreementStatus('loading');
      await handleAgreementSubmission(selectedPayment === null || selectedPayment === void 0 ? void 0 : selectedPayment.fundingSourceId, selectedBundleID);
      if (relationship.isSample) {
        reportToGtm('button-click', 'vendor-approved-test-agreement');
        reportToAppcues('button-click', 'vendor-approved-test-agreement');
        reportToHeap('testAgreement', 'click', 'vendor-approved-test-agreement');
        reportToHubspot('vendor_approved_test_agreement');
      } else {
        const paymentType = selectedPayment && selectedPayment.type === 'bankAccount' && (selectedPayment.status === 'new' || selectedPayment.status === 'validated') ? 'microDeposit' : selectedPayment === null || selectedPayment === void 0 ? void 0 : selectedPayment.type;
        reportToGtm('button-click', 'client-approved-agreement', {
          paymentType
        });
        reportToAppcues('button-click', 'client-approved-agreement');
        reportToHubspot('client-approved-agreement', {
          paymentType
        });
        reportToHeap('clientOb', 'click', 'client-approved-agreement', {
          paymentType
        });
      }
      setApproveAgreementStatus(null);
      const {
        relationship: updatedRelationship
      } = await refresh();
      relationshipModel.isPending(updatedRelationship) && navigateToPendingRelationshipPage();
    } catch (e) {
      setApproveAgreementStatus('error');
      if (!(e instanceof ErrorResponse) || !e.hasServerError(ServerError.InvalidRelationshipStatus)) overlayContext.showErrorToast(ErrorText.UnexpectedErrorTryAgain, 'approve-agreement-error-toast');
    }
  }
  function dynamicStepsRenderer(step) {
    switch (step) {
      case COBStep.welcome:
        {
          const {
            goNext
          } = navigateFrom(COBStep.welcome);
          return /*#__PURE__*/_jsx(WelcomeStep, {
            onDone: goNext
          });
        }
      case COBStep.selectAPackage:
        {
          const {
            goNext,
            goBack
          } = navigateFrom(COBStep.selectAPackage);
          return /*#__PURE__*/_jsx(PackagesStep, {
            selectedBundleID: selectedBundleID,
            onSelectBundleID: id => {
              setToStorage('selected_bundle_id', id);
              setSelectedBundleID(id);
              setSelectedOptionalServices(relationshipModel.includedByDefaultOptionalServices(relationship, id));
              goNext();
            },
            onClickNext: goNext,
            onClickBack: goBack
          });
        }
      case COBStep.reviewServices:
        {
          const {
            goNext,
            goBack
          } = navigateFrom(COBStep.reviewServices);
          return /*#__PURE__*/_jsx(ReviewServicesStep, {
            selectedBundleID: selectedBundleID,
            selectedOptionalServices: selectedOptionalServices,
            setSelectedOptionalServices: setSelectedOptionalServices,
            onDone: goNext,
            onBack: goBack
          });
        }
      case COBStep.termsOfService1:
      case COBStep.termsOfService2:
      case COBStep.termsOfService3:
      case COBStep.termsOfService4:
        {
          const {
            goNext,
            goBack
          } = navigateFrom(step);
          const index = termsOfServiceStepIndex[step];
          const currentTermsOfService = termsOfServices === null || termsOfServices === void 0 ? void 0 : termsOfServices[index];
          return /*#__PURE__*/_jsx(TermsOfServiceStep, {
            relationship: relationship,
            termsOfService: currentTermsOfService,
            onBack: goBack,
            onNext: goNext,
            isStatementAccepted: termsOfServiceStatements[index],
            onStatementChange: isAccepted => {
              setTermsOfServiceStatements(prev => {
                const newStatements = [...prev];
                newStatements[termsOfServiceStepIndex[step]] = isAccepted;
                return newStatements;
              });
            }
          });
        }
      case COBStep.approveTerms:
        {
          const {
            goNext,
            goBack
          } = navigateFrom(COBStep.approveTerms);
          const isLastStep = reachableSteps[reachableSteps.length - 2] === step;
          return /*#__PURE__*/_jsx(ApproveTermsStep, {
            selectedBundleID: selectedBundleID,
            selectedOptionalServices: selectedOptionalServices,
            previewStatus: previewStatus,
            onDone: goNext,
            onBack: goBack,
            disabledReason: isReviewer && isLastStep ? Reason.ReviewerApprove : !isPreview && isLastStep ? disabledReason : undefined,
            approveAgreement: approveAgreement,
            isLastStep: isLastStep,
            approveAgreementStatus: approveAgreementStatus
          });
        }
      case COBStep.accountantAccess:
        {
          const {
            goNext,
            goBack
          } = navigateFrom(COBStep.accountantAccess);
          return /*#__PURE__*/_jsx(AccountantAccessStep, {
            onBack: goBack,
            onDone: goNext
          });
        }
      case COBStep.paymentMethod:
        {
          const {
            goBack
          } = navigateFrom(COBStep.paymentMethod);
          return /*#__PURE__*/_jsx(ConnectPaymentMethodStep, {
            selectedPaymentId: selectedPayment === null || selectedPayment === void 0 ? void 0 : selectedPayment.fundingSourceId,
            isMemberClient: isMemberClient,
            previewStatus: previewStatus,
            onSelectPaymentMethod: changeSelectedPayment,
            onDone: approveAgreement,
            onBack: goBack,
            disabledReason: isReviewer ? Reason.ReviewerApprove : isPreview ? undefined : disabledReason,
            approveAgreementStatus: approveAgreementStatus
          });
        }
      case COBStep.allSet:
        return /*#__PURE__*/_jsx(PendingAdditionalSignaturesWithAnimations, {});
      case COBStep.introVideo:
        {
          const {
            goNext,
            goBack
          } = navigateFrom(COBStep.introVideo);
          return /*#__PURE__*/_jsx(IntroVideoStep, {
            onDone: goNext,
            onBack: goBack
          });
        }
      default:
        setCurrentStep(COBStep.welcome);
        return /*#__PURE__*/_jsx(_Fragment, {});
    }
  }
  const renderPreview = () => {
    return featureFlags.SET_UP_BRAND_CLIENT_PREVIEW ? /*#__PURE__*/_jsxs("div", {
      className: styles.headerContainer,
      children: [/*#__PURE__*/_jsx(Typography, {
        weight: "normal",
        skin: "invertedStandard",
        className: styles.previewBannerV2,
        "data-testid": "cob-preview-banner",
        children: "You are in preview mode. No activity will be saved."
      }), /*#__PURE__*/_jsxs("div", {
        className: styles.headerButtonsContainer,
        children: [previewState !== PreviewState.SetupBrand && /*#__PURE__*/_jsx(IconButton, {
          intent: Intent.BrandSetup,
          preferIntentTooltip: true,
          skin: "whiteAndPurple",
          className: "".concat(styles.brushBtn, " ").concat(localPreviewState === PreviewState.SetupBrand ? styles.brushBtnActive : ''),
          "data-testid": "client-preview-modal-toggle-brand-setup-btn",
          onClick: toggleBrandSetup,
          tooltip: {
            title: 'Customize brand'
          },
          children: /*#__PURE__*/_jsx(BrushIcon, {
            size: 24
          })
        }), /*#__PURE__*/_jsx(CloseButton, {
          "data-testid": "client-preview-modal-close-btn",
          skin: "whiteAndPurple",
          className: styles.closeBtn,
          onClick: dismissPreviewModal
        })]
      })]
    }) : /*#__PURE__*/_jsx(Typography, {
      weight: "normal",
      skin: "invertedStandard",
      className: styles.previewBanner,
      "data-testid": "cob-preview-banner",
      children: "You are in preview mode. No activity will be saved."
    });
  };
  return /*#__PURE__*/_jsxs("div", {
    "data-testid": "client-onboarding",
    className: styles.root,
    children: [isPreview ? renderPreview() : null, /*#__PURE__*/_jsx("div", {
      className: styles.main,
      children: /*#__PURE__*/_jsx(ResponsiveProvider, {
        disableMobile: disableMobile,
        children: /*#__PURE__*/_jsx(CobConfigProvider, {
          relationship: relationship,
          previewState: localPreviewState,
          originalPreviewState: previewState,
          isPreviewModal: !!(previewStatus !== null && previewStatus !== void 0 && previewStatus.inModal),
          children: /*#__PURE__*/_jsx(BrandingActionsProvider, {
            onToggleBrandSetup: toggleBrandSetup,
            children: /*#__PURE__*/_jsx(StripeElementsProvider, {
              children: /*#__PURE__*/_jsx(ClientFlowStructure, {
                hideBack: currentStep === COBStep.allSet,
                hideStepper: currentStep === COBStep.allSet,
                activeStepIndex: activeStepIndex,
                stepperSteps: reachableSteps.slice(0, -1).map(step => getStepName(step, relationship, Boolean(featureFlags.MULTIPLE_LEGAL_TERMS_DOCUMENTS))),
                completedSteps: completedSteps,
                onStepClick: i => reachableSteps[i] && setCurrentStep(reachableSteps[i]),
                "data-testid": "clientob-pane",
                activeSteps: [...new Set([...prevVisitedSteps, activeStepIndex])],
                children: dynamicStepsRenderer(currentStep)
              })
            })
          })
        })
      })
    })]
  });
}
export function ClientOnboardingRouted(props) {
  const {
    relationship
  } = props;
  const history = useHistory();
  const ff = useStore(selectors.ff.featureFlags);
  const businessId = useStore(selectors.user.businessId);
  const ffRelationshipVendorId = useStore(state => state.ff.relationshipVendorId);
  const {
    ff: {
      setRelationshipVendorId
    }
  } = useStoreActions();
  const {
    getFromStorage
  } = useCobStorage(relationship);
  const role = useStore(selectors.user.role);
  const userId = useStore(selectors.user.id);
  const fetchingFF = !ff.data || !ffRelationshipVendorId;
  const relationshipStatus = relationshipModel.status(relationship);
  const relationshipVendorId = relationshipModel.vendorID(relationship);
  const isVendor = useStore(selectors.user.isVendor);
  const [clientOnboardingNavigateState, dispatch] = useReducer(clientOnboardingNavigateStateReducer, {
    urlToNavigate: null,
    relationshipStatus: relationshipModel.status(relationship),
    role,
    relationshipId: relationshipModel.id(relationship),
    hasStartedWithPendingStatus: relationshipModel.isPending(relationship),
    isPrimary: relationshipModel.isBuyer(relationship, userId),
    vendorName: relationshipModel.vendorName(relationship),
    isVendor
  });
  useEffect(() => {
    if (clientOnboardingNavigateState.urlToNavigate) {
      const navigate = isAllowedToGoBack(clientOnboardingNavigateState.urlToNavigate) ? history.push : history.replace;
      navigate(clientOnboardingNavigateState.urlToNavigate, clientOnboardingNavigateState.state);
    }
  }, [clientOnboardingNavigateState.urlToNavigate, clientOnboardingNavigateState.state]);
  useEffect(() => {
    dispatch({
      type: 'RelationshipStatusChanged',
      status: relationshipStatus
    });
  }, [relationshipStatus]);
  useEffect(() => {
    setRelationshipVendorId(relationshipVendorId);
    return () => {
      setRelationshipVendorId();
    };
  }, [relationshipVendorId]);
  if (fetchingFF) {
    return /*#__PURE__*/_jsx(FunctionalLoader, {
      name: 'fetchFF',
      show: true
    });
  }
  return /*#__PURE__*/_jsxs(Switch, {
    children: [/*#__PURE__*/_jsx(Route, {
      path: "".concat(routes.clientOnboarding(), "/:stepId"),
      render: _ref => {
        let {
          history,
          match
        } = _ref;
        return /*#__PURE__*/_jsx(CobBrandingProvider, {
          initialBranding: relationship.vendor.branding,
          children: /*#__PURE__*/_jsx(ClientOnboarding, {
            previewState:
            // A vendor can enter here by copying the proposal-link and pasting it in a new tab while logged in.
            relationship && relationship.vendor.id === businessId && !relationship.isSample ? PreviewState.Preview : undefined,
            refresh: props.refresh,
            relationship: relationship,
            currentStep: match.params.stepId,
            setCurrentStep: to => history.push(to)
          })
        });
      }
    }), /*#__PURE__*/_jsx(Route, {
      path: routes.clientOnboarding(),
      children: _ref2 => {
        var _getFromStorage;
        let {
          match
        } = _ref2;
        return /*#__PURE__*/_jsx(Redirect, {
          to: "".concat(match.url, "/").concat((_getFromStorage = getFromStorage('last_step')) !== null && _getFromStorage !== void 0 ? _getFromStorage : COBStep.welcome)
        });
      }
    })]
  });
}
export function ClientOnboardingQueryWrapper(props) {
  const {
    relationshipId
  } = props.match.params;
  const businessId = useStore(selectors.user.businessId);
  const {
    api
  } = useStoreActions();
  const overlayContext = useOverlayContext();
  useDidUpdate(() => {
    overlayContext.stopLoader(changeBusinessLoader);
  }, [businessId]);
  async function fetchData() {
    const [relationship, membersResponse] = await Promise.all([api.relationship.get(relationshipId), api.member.getAll()]);
    const shouldSetAccountants = requestAccountantAccessModel.wasRequested(relationship.requestAccountantAccess);
    return {
      relationship,
      accountants: shouldSetAccountants ? membersResponse.accountants : []
    };
  }
  return /*#__PURE__*/_jsx(RestfulWrapper, {
    fetch: fetchData,
    renderChild: _ref3 => {
      let {
        data,
        refresh
      } = _ref3;
      return /*#__PURE__*/_jsx(ClientOnboardingRouted, {
        relationship: data.relationship,
        refresh: refresh
      });
    }
  });
}
export default function ClientOnboardingContextWrapper(props) {
  return /*#__PURE__*/_jsx(CobStorageProvider, {
    children: /*#__PURE__*/_jsx(ClientOnboardingQueryWrapper, _objectSpread({}, props))
  });
}